import { Button } from '@optools/components/ui/button';
import { Calendar } from '@optools/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { cn } from '@optools/components/ui/utils';
import { formatYearMonthDay } from '@optools/utils/datetime';
import { addDays, parseISO, subDays } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { ButtonGroup } from '../ui/button-group';

export interface DatePickerProps {
  date: string;
  disabled?: boolean;
  setDate: (date: string) => void;
}

export function DatePicker({ date, disabled = false, setDate }: DatePickerProps) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const dateObj: Date = parseISO(date);

  const onDateChange = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;

    const dateString = formatYearMonthDay(selectedDate);

    if (date === dateString) return;
    setDate(dateString);
    setIsCalendarOpen(false);
  };

  const onBackButtonClick = () => {
    const newDate = formatYearMonthDay(subDays(dateObj, 1));
    setDate(newDate);
  };

  const onForwardButtonClick = () => {
    const newDate = formatYearMonthDay(addDays(dateObj, 1));
    setDate(newDate);
  };

  return (
    <ButtonGroup className="min-w-max">
      <Button
        aria-label="previous day"
        disabled={disabled}
        size="icon"
        variant="outline"
        onClick={onBackButtonClick}>
        <ChevronLeft />
      </Button>
      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            aria-label="date calendar"
            className={cn(
              'min-w-36 items-center justify-center text-left font-normal',
              !date && 'text-muted-foreground',
            )}
            disabled={disabled}
            variant="outline">
            <CalendarIcon className="size-4" />
            <span>{date ? formatYearMonthDay(dateObj) : 'Pick a date'}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            defaultMonth={dateObj}
            mode="single"
            selected={dateObj}
            onSelect={onDateChange}
          />
        </PopoverContent>
      </Popover>
      <Button
        aria-label="next day"
        disabled={disabled}
        size="icon"
        variant="outline"
        onClick={onForwardButtonClick}>
        <ChevronRight />
      </Button>
    </ButtonGroup>
  );
}
